import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, X } from "lucide-react";
import { api } from "@/lib/api";

// Displays cancellation/reschedule/delete notifications addressed to this guest.
// Auto-polls; each chip has an X to dismiss. Complements <PendingInviteChip/>.
export default function GuestNotificationChip({ guestId }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!guestId) { setItems([]); return; }
    try {
      const { data } = await api.get("/public/guest-notifications", { params: { guest_id: guestId } });
      setItems(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [guestId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const dismiss = async (nid) => {
    setBusy(nid);
    try {
      await api.post(`/public/guest-notifications/${nid}/dismiss`, null, { params: { guest_id: guestId } });
      await load();
    } catch { toast.error("Could not dismiss"); }
    finally { setBusy(null); }
  };

  if (!items.length) return null;

  const tone = (k) =>
    k === "deleted" ? "border-destructive/40 bg-destructive/5"
    : k === "rescheduled" ? "border-accent/40 bg-accent/5"
    : k === "priority_claim_offer" ? "border-amber-500/60 bg-amber-500/10"
    : k === "priority_claim_bumped" ? "border-rose-500/50 bg-rose-500/5"
    : "border-amber-500/50 bg-amber-500/5";

  // Primary guest claims a bump-offer slot. Cancels the original booking
  // + couple sibling and creates a fresh one for the primary. Falls back
  // to a courteous error toast on 403/404.
  const claim = async (n) => {
    if (!n.booking_id) { toast.error("This offer expired — try again shortly"); return; }
    if (!window.confirm("Claim this timeslot? The current booking will be cancelled with a courteous notice.")) return;
    setBusy(n.id);
    try {
      await api.post("/public/bookings/priority-claim", { guest_id: guestId, booking_id: n.booking_id });
      toast.success("Slot claimed — a courtesy notice was sent to the other guest.");
      await dismiss(n.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim the slot");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2" data-testid="guest-notification-chips">
      {items.map((n) => (
        <div key={n.id} className={`flex items-start gap-3 border p-3 ${tone(n.kind)}`} data-testid={`guest-notif-${n.id}`}>
          <BellRing className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
          <div className="flex-1 min-w-0 text-sm space-y-2">
            <div>{n.message}</div>
            {n.kind === "priority_claim_offer" && n.booking_id && (
              <button
                onClick={() => claim(n)}
                disabled={busy === n.id}
                className="inline-flex items-center gap-1.5 h-8 px-3 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-full disabled:opacity-50"
                data-testid={`guest-notif-claim-${n.id}`}
              >
                {busy === n.id ? "Claiming…" : "Priority claim this slot"}
              </button>
            )}
          </div>
          <button
            onClick={() => dismiss(n.id)}
            disabled={busy === n.id}
            className="text-muted-foreground hover:text-foreground disabled:opacity-40"
            data-testid={`guest-notif-dismiss-${n.id}`}
            aria-label="Dismiss"
          ><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}
